import React, { useState, useEffect, useCallback } from 'react';
import { LanguageProvider, useLanguage } from './LanguageContext';
import { NotificationProvider, useNotificationNav, NotificationNavTarget } from './NotificationContext';
import { el as elStrings, en as enStrings } from './i18n/app';
import axios from 'axios';
import { PLAN_CATALOG } from './planConfig';
import Dashboard from './Dashboard';
import ProjectHub from './ProjectHub';
import UserManagement from './UserManagement';
import ProfileEdit from './ProfileEdit';
import OrganizationSettings from './OrganizationSettings';
import OnboardingWizard from './OnboardingWizard';
import SuperAdminPanel from './SuperAdminPanel';
import LandingPage from './LandingPage';
import ProjectCharter from './modules/ProjectCharter';
import RiskManagement from './modules/RiskManagement';
import OrganizationChart from './modules/OrganizationChart';
import Schedule from './modules/Schedule';
import Stakeholders from './modules/Stakeholders';
import Issues from './modules/Issues';
import DocumentsModule from './modules/DocumentsModule';
import KickoffPresentation from './modules/KickoffPresentation';
import TranscriptModule from './modules/TranscriptModule';
import Meetings from './modules/Meetings';
import AIAdvisor from './modules/AIAdvisor';
import ProjectSpecs from './modules/ProjectSpecs';
import DailyLog from './modules/DailyLog';
import Scope from './modules/Scope';
import Budget from './modules/Budget';
import Reporting from './modules/Reporting';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';
// Unconditional full access. PM is NOT here — full module rights, but only within
// projects they're actually assigned to (verified via project_members, not role alone).
const TRUE_FULL_ACCESS_ROLES = ['admin', 'pmo'];

function App() {
  const { language } = useLanguage();
  const L = language === 'en' ? enStrings : elStrings;

  const [user, setUser] = useState<any>(() => {
    const saved = localStorage.getItem('user');
    return saved ? JSON.parse(saved) : null;
  });
  const [token, setToken] = useState<string>(() => localStorage.getItem('token') || '');
  const [selectedProject, setSelectedProject] = useState<any>(() => {
    const saved = localStorage.getItem('selectedProject');
    return saved ? JSON.parse(saved) : null;
  });
  const [selectedModule, setSelectedModule] = useState<string | null>(() =>
    localStorage.getItem('selectedModule') || null
  );
  const [moduleOpenCreate, setModuleOpenCreate] = useState(false);
  const [moduleOpenTaskId, setModuleOpenTaskId] = useState<string | undefined>(undefined);
  const { setNavigateHandler } = useNotificationNav();
  const [projectReadOnly, setProjectReadOnly] = useState(false);
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [createOrgName, setCreateOrgName] = useState('');
  const [createOrgPlan, setCreateOrgPlan] = useState<'trial' | 'starter' | 'pro' | 'enterprise' | null>(null);
  const [createOrgLoading, setCreateOrgLoading] = useState(false);
  const [createOrgError, setCreateOrgError] = useState('');
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showOrgSettings, setShowOrgSettings] = useState(false);
  const [showSuperAdmin, setShowSuperAdmin] = useState(false);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [renewalStep, setRenewalStep] = useState<'expired' | 'plans'>('expired');
  const [renewalLoading, setRenewalLoading] = useState<string | null>(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [homeOrgId, setHomeOrgId] = useState<string>(() =>
    localStorage.getItem('homeOrgId') ||
    (() => { const s = localStorage.getItem('user'); return s ? JSON.parse(s)?.organization_id ?? '' : ''; })()
  );
  const [projects, setProjects] = useState<any[]>([]);
  const [permissions, setPermissions] = useState<any[]>([]);
  const [stripePrices, setStripePrices] = useState<Record<string, number | null>>({});
  const [dbPlanLimits, setDbPlanLimits] = useState<Record<string, { maxProjects: number | null; maxUsers: number | null }>>({});

  // Φόρτωση live τιμών και ορίων πλάνων από backend (χωρίς auth)
  useEffect(() => {
    axios.get(`${API}/payments/prices`).then(r => setStripePrices(r.data)).catch(() => {});
    axios.get(`${API}/payments/plan-limits`).then(r => setDbPlanLimits(r.data)).catch(() => {});
  }, []);

  // Έλεγχος συνδρομής κατά το mount — fresh check από backend για existing sessions
  useEffect(() => {
    if (!user || !token || user.role === 'super_admin') {
      if (user && isSubscriptionExpired(user)) {
        const params = new URLSearchParams(window.location.search);
        if (params.get('payment') !== 'success') {
          setShowRenewalModal(true);
          setRenewalStep(user.is_trial ? 'plans' : 'expired');
        }
      }
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const paymentSuccess = params.get('payment') === 'success';
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const fresh = { ...user, org_plan: res.data.org_plan, org_has_subscription: res.data.org_has_subscription, is_trial: res.data.is_trial };
        setUser(fresh);
        localStorage.setItem('user', JSON.stringify(fresh));
        if (!paymentSuccess && isSubscriptionExpired(fresh)) {
          setShowRenewalModal(true);
          setRenewalStep(fresh.is_trial ? 'plans' : 'expired');
        }
      })
      .catch(() => {
        if (isSubscriptionExpired(user)) {
          setShowRenewalModal(true);
          setRenewalStep(user.is_trial ? 'plans' : 'expired');
        }
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // PM has full module rights, but only within projects they're actually a member
  // of — verified against project_members, never assumed from role alone.
  const [isPmProjectMember, setIsPmProjectMember] = useState(false);

  // Φόρτωση permissions όταν αλλάζει το project
  useEffect(() => {
    if (!user || !selectedProject) { setPermissions([]); setIsPmProjectMember(false); return; }
    if (TRUE_FULL_ACCESS_ROLES.includes(user.role)) { setPermissions([]); return; } // full access — δεν χρειάζεται
    if (user.role === 'pm') {
      axios.get(`${API}/projects/${selectedProject.id}/members`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(res => setIsPmProjectMember((res.data || []).includes(user.id)))
        .catch(() => setIsPmProjectMember(false));
      return;
    }

    axios.get(`${API}/users/${user.id}/permissions/${selectedProject.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(res => setPermissions(res.data || []))
      .catch(() => setPermissions([]));
  }, [user, selectedProject, token]);

  const isFullProjectAccess = (role: string) =>
    TRUE_FULL_ACCESS_ROLES.includes(role) || (role === 'pm' && isPmProjectMember);

  // Helper: έλεγχος can_edit για συγκεκριμένο module
  const canEdit = useCallback((moduleId: string): boolean => {
    if (!user) return false;
    if (projectReadOnly) return false;
    if (isFullProjectAccess(user.role)) return true;
    const perm = permissions.find(p => p.module === moduleId);
    return perm?.can_edit === true;
  }, [user, permissions, projectReadOnly, isPmProjectMember]);

  // Helper: έλεγχος can_view για συγκεκριμένο module
  const canView = useCallback((moduleId: string): boolean => {
    if (!user) return false;
    if (isFullProjectAccess(user.role)) return true;
    const perm = permissions.find(p => p.module === moduleId);
    return perm?.can_view === true;
  }, [user, permissions, isPmProjectMember]);

  const isSubscriptionExpired = (userData: any) => {
    if (!userData || userData.role === 'super_admin') return false;
    return !userData.org_has_subscription || userData.org_plan === 'cancelled';
  };

  const handleLogin = (userData: any, accessToken: string) => {
    setUser(userData);
    setToken(accessToken);
    setHomeOrgId(userData.organization_id ?? '');
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('homeOrgId', userData.organization_id ?? '');
    if (isSubscriptionExpired(userData)) {
      setShowRenewalModal(true);
      setRenewalStep(userData.is_trial ? 'plans' : 'expired');
    }
  };

  // Detect ?gmail=connected after Gmail OAuth redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail') === 'connected') {
      window.history.replaceState({}, '', '/');
      setSelectedModule('Project Specs');
    }
  }, []);

  // Detect ?payment=success after Stripe redirect — poll until webhook updates DB (race condition fix)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') !== 'success' || !user || !token) return;
    window.history.replaceState({}, '', '/');
    setPaymentProcessing(true);

    const MAX_ATTEMPTS = 15; // 15 × 2s = 30s max
    let attempts = 0;

    const poll = () => {
      axios.get(`${API}/organizations/my/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(res => {
        if (res.data.has_subscription) {
          const updated = { ...user, org_plan: res.data.plan, org_has_subscription: true };
          setUser(updated);
          localStorage.setItem('user', JSON.stringify(updated));
          setShowRenewalModal(false);
          setPaymentProcessing(false);
        } else {
          attempts++;
          if (attempts < MAX_ATTEMPTS) {
            setTimeout(poll, 2000);
          } else {
            // Timeout — still show updated plan even if webhook is slow
            const updated = { ...user, org_plan: res.data.plan, org_has_subscription: res.data.has_subscription };
            setUser(updated);
            localStorage.setItem('user', JSON.stringify(updated));
            setPaymentProcessing(false);
          }
        }
      }).catch(() => {
        attempts++;
        if (attempts < MAX_ATTEMPTS) setTimeout(poll, 2000);
        else setPaymentProcessing(false);
      });
    };

    poll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRenewalCheckout = async (plan: string) => {
    setRenewalLoading(plan);
    try {
      const res = await axios.post(`${API}/payments/renewal-checkout`, { plan }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.location.href = res.data.url;
    } catch {
      alert(L.app.errorCheckout);
    } finally {
      setRenewalLoading(null);
    }
  };

  const handleOnboardingComplete = () => {
    const updated = { ...user, onboarded: true };
    setUser(updated);
    localStorage.setItem('user', JSON.stringify(updated));
  };

  const handleLogout = () => {
    setUser(null); setToken(''); setSelectedProject(null);
    setSelectedModule(null); setShowUserManagement(false);
    setShowProfileEdit(false); setPermissions([]);
    localStorage.removeItem('token'); localStorage.removeItem('user');
    localStorage.removeItem('selectedProject'); localStorage.removeItem('selectedModule');
    localStorage.removeItem('homeOrgId');
  };

  const handleSwitchOrg = async (orgId: string) => {
    try {
      const res = await axios.post(
        `${API}/auth/switch-org`,
        { organization_id: orgId },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { user: newUser, access_token: newToken } = res.data;
      setUser(newUser);
      setToken(newToken);
      setSelectedProject(null);
      setSelectedModule(null);
      setPermissions([]);
      localStorage.setItem('user', JSON.stringify(newUser));
      localStorage.setItem('token', newToken);
      localStorage.removeItem('selectedProject');
      localStorage.removeItem('selectedModule');
      return { user: newUser, token: newToken };
    } catch (e) {
      console.error('Switch org failed:', e);
      return null;
    }
  };

  const handleSelectProject = (project: any) => {
    setSelectedProject(project);
    setSelectedModule(null);
    localStorage.setItem('selectedProject', JSON.stringify(project));
    localStorage.removeItem('selectedModule');
  };

  const handleSelectModule = (module: string, openCreate = false, openTaskId?: string) => {
    setSelectedModule(module);
    setModuleOpenCreate(openCreate);
    setModuleOpenTaskId(openTaskId);
    localStorage.setItem('selectedModule', module);
  };

  const handleNotificationNavigate = async (target: NotificationNavTarget) => {
    let activeUser = user;
    let activeToken = token;
    if (target.organization_id && target.organization_id !== user?.organization_id) {
      const switched = await handleSwitchOrg(target.organization_id);
      if (switched) { activeUser = switched.user; activeToken = switched.token; }
    }
    if (target.project_id) {
      handleSelectProject({ id: target.project_id, name: target.project_name });

      // Admin/PMO: unconditional. PM: must actually be a member of THIS project
      // (no longer full-access by role alone). Everyone else: per-module permission row.
      let canViewDailyLog = ['admin', 'pmo'].includes(activeUser?.role);
      if (!canViewDailyLog && activeUser?.role === 'pm') {
        try {
          const res = await axios.get(`${API}/projects/${target.project_id}/members`, {
            headers: { Authorization: `Bearer ${activeToken}` },
          });
          canViewDailyLog = (res.data || []).includes(activeUser.id);
        } catch {
          canViewDailyLog = false;
        }
      } else if (!canViewDailyLog && target.task_id) {
        try {
          const res = await axios.get(`${API}/users/${activeUser.id}/permissions/${target.project_id}`, {
            headers: { Authorization: `Bearer ${activeToken}` },
          });
          canViewDailyLog = (res.data || []).some((p: any) => p.module === 'Daily Log' && p.can_view === true);
        } catch {
          canViewDailyLog = false;
        }
      }

      if (target.task_id && canViewDailyLog) handleSelectModule('Daily Log', false, target.task_id);
      // else: lands on the Project Hub only (no module selected) — either no task_id,
      // or the user has no view permission for Daily Log in this project
    }
    // else: org-level invite only — handleSwitchOrg already landed on the Dashboard
  };
  setNavigateHandler(handleNotificationNavigate);

  const handleBackToHub = () => {
    setSelectedModule(null);
    localStorage.removeItem('selectedModule');
  };

  const handleCreateOrg = async () => {
    if (!createOrgName.trim() || !createOrgPlan) return;
    setCreateOrgLoading(true);
    setCreateOrgError('');
    try {
      // Step 1: create org (always starts as trial to get org id + jwt)
      const createRes = await axios.post(`${API}/auth/create-org`, { orgName: createOrgName.trim() }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const newToken = createRes.data.access_token;
      const newUser = createRes.data.user;

      if (createOrgPlan === 'trial') {
        setShowCreateOrg(false);
        setCreateOrgName('');
        setCreateOrgPlan(null);
        handleLogin(newUser, newToken);
      } else {
        // Step 2: create Stripe checkout for the new org using the new JWT
        const checkoutRes = await axios.post(`${API}/payments/renewal-checkout`, { plan: createOrgPlan }, {
          headers: { Authorization: `Bearer ${newToken}` },
        });
        // Update state to new org context before redirecting
        handleLogin(newUser, newToken);
        window.location.href = checkoutRes.data.url;
      }
    } catch (err: any) {
      setCreateOrgError(err?.response?.data?.message ?? 'Σφάλμα κατά τη δημιουργία οργανισμού');
      setCreateOrgLoading(false);
    }
  };

  const handleSelectProjectReadOnly = (project: any) => {
    setSelectedProject(project);
    setSelectedModule(null);
    setProjectReadOnly(true);
  };

  const handleBackToDashboard = async () => {
    setProjectReadOnly(false);
    if (homeOrgId && user?.organization_id !== homeOrgId) {
      await handleSwitchOrg(homeOrgId);
    } else {
      setSelectedProject(null); setSelectedModule(null); setPermissions([]);
      localStorage.removeItem('selectedProject'); localStorage.removeItem('selectedModule');
    }
  };

  if (!user) return <LandingPage onLogin={handleLogin} />;

  // Payment processing screen — polling webhook confirmation
  if (paymentProcessing) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb', gap: 16 }}>
      <div style={{ width: 48, height: 48, border: '4px solid #e5e7eb', borderTop: '4px solid #4f46e5', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <p style={{ fontSize: 16, fontWeight: 600, color: '#1a1a2e', margin: 0 }}>Επεξεργασία πληρωμής...</p>
      <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>Παρακαλώ περιμένετε, επιβεβαιώνουμε την πληρωμή σας.</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  // Subscription expired modal — εμφανίζεται πάνω από τα πάντα
  if (showRenewalModal) return (
    <div onClick={handleLogout} style={{ minHeight: '100vh', backgroundColor: '#0a0a1e', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'pointer' }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: 16, padding: 40, width: '100%', maxWidth: renewalStep === 'plans' ? 720 : 440, boxShadow: '0 20px 60px rgba(0,0,0,0.4)', cursor: 'default' }}>
        {renewalStep === 'expired' ? (
          <>
            <div style={{ width: 64, height: 64, borderRadius: '50%', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', fontSize: 28 }}>⚠️</div>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e', textAlign: 'center', margin: '0 0 12px' }}>{L.app.subscriptionExpiredTitle}</h2>
            <p style={{ fontSize: 14, color: '#6b7280', textAlign: 'center', margin: '0 0 8px' }}>
              {L.app.subscriptionExpiredDesc}
            </p>
            <p style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', margin: '0 0 32px' }}>
              {L.app.subscriptionExpiredNote}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={() => setRenewalStep('plans')}
                style={{ padding: '13px', backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' }}
              >
                {L.app.renewBtn}
              </button>
              <button
                onClick={handleLogout}
                style={{ padding: '11px', backgroundColor: 'transparent', color: '#9ca3af', border: '1px solid #e5e7eb', borderRadius: 10, fontSize: 14, cursor: 'pointer' }}
              >
                {L.app.logout}
              </button>
            </div>
          </>
        ) : (
          <>
            {user?.is_trial ? (
              <>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(79,70,229,0.12), rgba(139,92,246,0.1))', border: '1px solid rgba(79,70,229,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px', fontSize: 24 }}>✦</div>
                <h2 style={{ fontSize: 22, fontWeight: 700, color: '#1a1a2e', textAlign: 'center', margin: '0 0 8px' }}>{L.app.trialExpiredTitle}</h2>
                <p style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', margin: '0 0 28px' }}>{L.app.trialExpiredDesc}</p>
              </>
            ) : (
              <>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a1a2e', margin: '0 0 6px' }}>{L.app.selectPlanTitle}</h2>
                <p style={{ fontSize: 13, color: '#9ca3af', margin: '0 0 24px' }}>{L.app.selectPlanDesc}</p>
              </>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 20 }}>
              {PLAN_CATALOG.map(p => {
                const livePrice = stripePrices[p.id] != null ? stripePrices[p.id]! : parseInt(p.priceNumber);
                const dbLimits = dbPlanLimits[p.id];
                const maxProjects = dbLimits ? dbLimits.maxProjects : p.maxProjects;
                const maxUsers = dbLimits ? dbLimits.maxUsers : p.maxUsers;
                const limitsText = L.app.planLimitsText(maxProjects, maxUsers);
                return (
                  <div key={p.id} style={{ border: p.highlighted ? '2px solid #4f46e5' : '1px solid #e5e7eb', borderRadius: 12, padding: '20px 14px', textAlign: 'center', position: 'relative', background: p.highlighted ? 'rgba(79,70,229,0.03)' : '#fff' }}>
                    {p.highlighted && (
                      <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', backgroundColor: '#4f46e5', color: 'white', fontSize: 10, fontWeight: 700, padding: '3px 12px', borderRadius: 20, whiteSpace: 'nowrap' }}>{L.app.popular}</div>
                    )}
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 6 }}>{p.name}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 2, marginBottom: 4 }}>
                      <span style={{ fontSize: 26, fontWeight: 800, color: '#4f46e5' }}>€{livePrice}</span>
                      <span style={{ fontSize: 12, color: '#9ca3af' }}>{L.app.perMonth}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14 }}>{limitsText}</div>
                    <button
                      onClick={() => handleRenewalCheckout(p.id)}
                      disabled={renewalLoading !== null}
                      style={{ width: '100%', padding: '10px', backgroundColor: p.highlighted ? '#4f46e5' : '#f3f4f6', color: p.highlighted ? 'white' : '#374151', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
                    >
                      {renewalLoading === p.id ? L.app.redirecting : L.app.select}
                    </button>
                  </div>
                );
              })}
            </div>
            {!user?.is_trial && (
              <button onClick={() => setRenewalStep('expired')} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 13, cursor: 'pointer', padding: 0 }}>
                {L.app.backBtn}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );

  // Onboarding wizard για νέους admin (provisioned μέσω Stripe)
  const needsOnboarding = user.role === 'admin' && !user.onboarded;

  if (showSuperAdmin || user.role === 'super_admin') return (
    <SuperAdminPanel token={token} onBack={user.role === 'super_admin' ? handleLogout : () => setShowSuperAdmin(false)} onLogout={handleLogout} />
  );

  // Κοινά props για όλα τα modules
  const moduleProps = {
    project: selectedProject, user, token, onBack: handleBackToHub,
    onLogout: handleLogout, onProfileEdit: () => setShowProfileEdit(true),
    openCreate: moduleOpenCreate,
    openTaskId: moduleOpenTaskId,
    onProjectUpdate: (updated: any) => {
      setSelectedProject(updated);
      localStorage.setItem('selectedProject', JSON.stringify(updated));
    },
  };

  return (
    <>
      {needsOnboarding && (
        <OnboardingWizard user={user} token={token} onComplete={handleOnboardingComplete} />
      )}

      {showProfileEdit && (
        <ProfileEdit user={user} token={token} onClose={() => setShowProfileEdit(false)} />
      )}

      {showCreateOrg && (() => {
        const canSubmit = !!createOrgName.trim() && !!createOrgPlan && !createOrgLoading;
        const btnLabel = createOrgLoading
          ? L.createOrg.creating
          : createOrgPlan === 'trial'
            ? L.createOrg.btnTrial
            : L.createOrg.btnPaid;

        return (
          <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
            onClick={() => { if (!createOrgLoading) { setShowCreateOrg(false); setCreateOrgName(''); setCreateOrgPlan(null); setCreateOrgError(''); } }}>
            <div style={{ backgroundColor: 'white', borderRadius: 18, padding: '32px 36px', width: '100%', maxWidth: 500, boxShadow: '0 24px 64px rgba(0,0,0,0.28)' }}
              onClick={e => e.stopPropagation()}>

              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, backgroundColor: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>🏢</div>
                <div>
                  <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: 0 }}>{L.createOrg.title}</h2>
                  <p style={{ fontSize: 12, color: '#9ca3af', margin: '2px 0 0' }}>{L.createOrg.subtitle}</p>
                </div>
              </div>

              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>{L.createOrg.nameLabel}</label>
              <input
                type="text"
                value={createOrgName}
                onChange={e => { setCreateOrgName(e.target.value); setCreateOrgError(''); }}
                placeholder={L.createOrg.namePh}
                maxLength={80}
                autoFocus
                style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box', marginBottom: 20 }}
              />

              <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>{L.createOrg.choosePlan}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                {/* Trial option */}
                {[
                  { id: 'trial' as const, label: L.createOrg.trialName, sub: L.createOrg.trialSub, price: L.createOrg.trialPrice, badge: null },
                  ...PLAN_CATALOG.map(p => ({
                    id: p.id as 'starter' | 'pro' | 'enterprise',
                    label: p.name,
                    sub: p.desc,
                    price: `€${stripePrices[p.id] ?? p.priceNumber}/μήνα`,
                    badge: p.highlighted ? L.createOrg.popular : null,
                  }))
                ].map(opt => {
                  const selected = createOrgPlan === opt.id;
                  return (
                    <div key={opt.id} onClick={() => { setCreateOrgPlan(opt.id); setCreateOrgError(''); }}
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderRadius: 10, border: selected ? '2px solid #4f46e5' : '1px solid #e5e7eb', backgroundColor: selected ? 'rgba(79,70,229,0.04)' : 'white', cursor: 'pointer', transition: 'border-color 0.15s' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 18, height: 18, borderRadius: '50%', border: selected ? '5px solid #4f46e5' : '2px solid #d1d5db', flexShrink: 0, transition: 'border 0.15s' }} />
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a2e' }}>{opt.label}</span>
                            {opt.badge && <span style={{ fontSize: 10, fontWeight: 700, backgroundColor: '#4f46e5', color: 'white', padding: '1px 7px', borderRadius: 999 }}>{opt.badge}</span>}
                          </div>
                          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>{opt.sub}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: opt.id === 'trial' ? '#059669' : '#4f46e5', whiteSpace: 'nowrap', flexShrink: 0 }}>{opt.price}</div>
                    </div>
                  );
                })}
              </div>

              {createOrgError && (
                <p style={{ fontSize: 12, color: '#ef4444', margin: '-12px 0 14px' }}>{createOrgError}</p>
              )}

              <button
                onClick={handleCreateOrg}
                disabled={!canSubmit}
                style={{ width: '100%', padding: '13px', backgroundColor: canSubmit ? '#4f46e5' : '#a5b4fc', color: 'white', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
              >
                {btnLabel}
              </button>
            </div>
          </div>
        );
      })()}

      {showOrgSettings && (
        <OrganizationSettings user={user} token={token} onClose={() => setShowOrgSettings(false)} onPlanChanged={(newPlan) => {
          const updated = { ...user, org_plan: newPlan };
          setUser(updated);
          localStorage.setItem('user', JSON.stringify(updated));
        }} />
      )}

      {selectedProject && projectReadOnly && (
        <>
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, backgroundColor: '#f59e0b', color: '#1c1917', padding: '8px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px', fontWeight: '600', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
            <span>Προβολή μόνο ανάγνωσης — "{selectedProject.name}"</span>
            <button onClick={handleBackToDashboard} style={{ background: 'rgba(0,0,0,0.15)', border: 'none', borderRadius: '6px', padding: '4px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', color: '#1c1917' }}>
              ← Επιστροφή
            </button>
          </div>
          <div style={{ height: '40px' }} />
        </>
      )}

      {showUserManagement ? (
        <UserManagement user={user} token={token} projects={projects} onBack={() => setShowUserManagement(false)} onLogout={handleLogout} onProfileEdit={() => setShowProfileEdit(true)} onUserUpdated={(updated) => { setUser(updated); localStorage.setItem('user', JSON.stringify(updated)); }} />
      ) : selectedProject && selectedModule === 'Project Charter' ? (
        <ProjectCharter {...moduleProps} canEdit={canEdit('Project Charter')} />
      ) : selectedProject && selectedModule === 'Kickoff Presentation' ? (
        <KickoffPresentation {...moduleProps} canEdit={canEdit('Kickoff Presentation')} />
      ) : selectedProject && selectedModule === 'Transcripts' ? (
        <TranscriptModule {...moduleProps} canEdit={canEdit('Transcripts')} />
      ) : selectedProject && selectedModule === 'Meetings' ? (
        <Meetings {...moduleProps} canEdit={canEdit('Meetings')} />
      ) : selectedProject && selectedModule === 'AI Advisor' ? (
        <AIAdvisor {...moduleProps} canEdit={canEdit('AI Advisor')} />
      ) : selectedProject && selectedModule === 'Project Specs' ? (
        <ProjectSpecs {...moduleProps} canEdit={canEdit('Project Specs')} />
      ) : selectedProject && selectedModule === 'Risk Management' ? (
        <RiskManagement {...moduleProps} canEdit={canEdit('Risk Management')} />
      ) : selectedProject && selectedModule === 'Organization Chart' ? (
        <OrganizationChart {...moduleProps} canEdit={canEdit('Organization Chart')} />
      ) : selectedProject && selectedModule === 'Schedule' ? (
        <Schedule {...moduleProps} canEdit={canEdit('Schedule')} />
      ) : selectedProject && selectedModule === 'Stakeholders' ? (
        <Stakeholders {...moduleProps} canEdit={canEdit('Stakeholders')} />
      ) : selectedProject && selectedModule === 'Issues' ? (
        <Issues {...moduleProps} canEdit={canEdit('Issues')} />
      ) : selectedProject && selectedModule === 'Documents' ? (
        <DocumentsModule {...moduleProps} canEdit={canEdit('Documents')} />
      ) : selectedProject && selectedModule === 'Reporting' ? (
        <Reporting {...moduleProps} canEdit={canEdit('Reporting')} />
      ) : selectedProject && selectedModule === 'Daily Log' ? (
        <DailyLog {...moduleProps} canEdit={canEdit('Daily Log')} onBack={() => setSelectedModule(null)} />
      ) : selectedProject && selectedModule === 'Scope' ? (
        <Scope {...moduleProps} canEdit={canEdit('Scope')} />
      ) : selectedProject && selectedModule === 'Budget' ? (
        <Budget {...moduleProps} canEdit={canEdit('Budget')} />
      ) : selectedProject ? (
        <ProjectHub
          project={selectedProject}
          user={user}
          token={token}
          onBack={handleBackToDashboard}
          onSelectModule={handleSelectModule}
          onProfileEdit={() => setShowProfileEdit(true)}
          onLogout={handleLogout}
          canViewAIAssistant={canView('AI Assistant')}
          onProjectRenamed={newName => {
            const updated = { ...selectedProject, name: newName };
            setSelectedProject(updated);
            localStorage.setItem('selectedProject', JSON.stringify(updated));
          }}
        />
      ) : (
        <Dashboard
          user={user}
          token={token}
          onLogout={handleLogout}
          onSelectProject={handleSelectProject}
          onUserManagement={() => setShowUserManagement(true)}
          onProjectsLoaded={setProjects}
          onProfileEdit={() => setShowProfileEdit(true)}
          onOrgSettings={() => setShowOrgSettings(true)}
          onSuperAdmin={() => setShowSuperAdmin(true)}
          onSwitchOrg={handleSwitchOrg}
          onViewProjectReadOnly={handleSelectProjectReadOnly}
          onCreateOrg={() => { setCreateOrgName(''); setCreateOrgError(''); setShowCreateOrg(true); }}
        />
      )}
    </>
  );
}

const AppWithProviders: React.FC = () => (
  <LanguageProvider>
    <NotificationProvider>
      <App />
    </NotificationProvider>
  </LanguageProvider>
);

export default AppWithProviders;
